import React, {useEffect, useMemo, useState} from 'react';
import {Redirect, Route, Switch, withRouter} from "react-router-dom";
import {useBootstrap, useEntities, useFilters, useProgress} from "@atttomyx/shared-hooks";
import {usePushEvent} from "@atttomyx/react-hooks";
import {
    AboutPage,
    ChangePasswordPage,
    ForgotPasswordPage,
    JoinAccountPage,
    LoginPage,
    MyAccountPage,
    NewUserPage,
    NotFoundPage,
    PhoneStatusBar,
    ProfilePage,
    PublicHeader,
    Splash,
    UsersPage,
    WelcomePage,
} from "@atttomyx/react-components";
import {UserCard} from "./components/cards";
import {UserFilters} from "./components/filters";
import {AccountSettingsForm, UserSettingsForm} from "./components/forms";
import {Footer} from "./components/nav";
import {
    AddNodePage,
    AddUserPage,
    CalendarPage,
    EditNodePage,
    EditUserPage,
    NodesPage,
    OfferingsPage,
} from "./components/pages";
import {
    accountService,
    appService,
    authService,
    edgeService,
    nodeService,
    notificationService,
    offeringService,
    profileService,
    taskService,
    userService,
} from "./services";
import {datetime, objects, sorting} from "@atttomyx/shared-utils";
import {branding as brandingUtils, firebase as firebaseUtils, router, timezones} from "@atttomyx/react-utils";
import {graphUtils, offeringUtils} from "./utils";
import {getOfferingDefaults, getOfferingFilters, NODE_FILTERS, USER_FILTERS} from "./filters";
import {themes, time} from "@atttomyx/shared-constants";
import {
    APP_NAME,
    APP_TAG_LINE,
    DATE_YEARLY,
    FIREBASE_CONFIG,
    PAGE_ABOUT,
    PAGE_ACCOUNT,
    PAGE_CALENDAR,
    PAGE_HOME,
    PAGE_JOIN_ACCOUNT,
    PAGE_LOGIN,
    PAGE_NEW_USER,
    PAGE_NODE,
    PAGE_NODES,
    PAGE_OFFERINGS,
    PAGE_PASSWORD,
    PAGE_RECOVERY,
    PAGE_SETTINGS,
    PAGE_USER,
    PAGE_USERS,
    PAGE_WELCOME,
    PATH_DEEP_LINK,
    PRIVATE_PAGES_EXACT,
    PRIVATE_PAGES_STARTS_WITH,
    PUBLIC_PAGES_EXACT,
    PUBLIC_PAGES_STARTS_WITH,
} from "./constants";
import icon from "./icon.png";

const MOCK_PHONE = false;

const App = (props) => {
    const { snackbar, dimensions, history, onThemeChange } = props;
    const [ date, setDate ] = useState(datetime.today());

    const users = useEntities(userService.listUsers, snackbar.setError, "users", sorting.sortByLastNameAndFirstName);
    const nodes = useEntities(nodeService.listNodes, snackbar.setError, "nodes", sorting.sortByLastNameAndFirstName);
    const edges = useEntities(edgeService.listEdges, snackbar.setError, "edges", sorting.sortByUpdatedDesc);
    const tasks = useEntities(taskService.listTasks, snackbar.setError, "tasks", sorting.sortByUpdatedDesc);
    const offerings = useEntities(offeringService.listOfferings, snackbar.setError, "offerings", offeringUtils.sortOfferings);

    const userFilters = useFilters(USER_FILTERS);
    const nodeFilters = useFilters(NODE_FILTERS);
    const offeringFilters = useFilters(getOfferingFilters(nodes.entities), getOfferingDefaults());

    const prep1 = useMemo(() => {
        const phones = {};
        const emails = {};
        const urls = {};
        const addresses = {};
        const dates = {};
        const others = {};
        const tags = {};

        nodes.entities.forEach((node) => {
            node.phones.forEach((phone) => phones[phone.label] = true);
            node.emails.forEach((email) => emails[email.label] = true);
            node.urls.forEach((url) => urls[url.label] = true);
            node.addresses.forEach((address) => addresses[address.label] = true);
            node.dates.forEach((date) => dates[date.label] = true);
            node.others.forEach((other) => others[other.label] = true);
            node.tags.forEach((tag) => tags[tag] = true);
        });

        return {
            phones: Object.keys(phones).sort(sorting.alphabetize),
            emails: Object.keys(emails).sort(sorting.alphabetize),
            urls: Object.keys(urls).sort(sorting.alphabetize),
            addresses: Object.keys(addresses).sort(sorting.alphabetize),
            dates: Object.keys(dates).sort(sorting.alphabetize),
            others: Object.keys(others).sort(sorting.alphabetize),
            tags: Object.keys(tags).sort(sorting.alphabetize),
        };
    }, [nodes.entities]);

    const prep2 = useMemo(() => {
        const categories = {};
        const types = {};

        offerings.entities.forEach((offering) => {
            categories[offering.category] = true;
            types[offering.type] = true;
        });

        return {
            categories: Object.keys(categories).sort(sorting.alphabetize),
            types: Object.keys(types).sort(sorting.alphabetize),
        };
    }, [offerings.entities]);

    const unique = useMemo(() => {
        return {
            phones: prep1.phones,
            emails: prep1.emails,
            urls: prep1.urls,
            addresses: prep1.addresses,
            dates: prep1.dates,
            others: prep1.others,
            tags: prep1.tags,
            categories: prep2.categories,
            types: prep2.types,
        };
    }, [prep1, prep2]);

    const households = useMemo(() => {
        return graphUtils.getNodeIdToConnectedNodeIds(nodes.entities, edges.entities);
    }, [nodes.entities, edges.entities]);

    const clearAll = () => {
        progress.clear();
        bootstrap.clear();
        users.clear();
        nodes.clear();
        edges.clear();
        tasks.clear();
        offerings.clear();
        userFilters.clear();
        nodeFilters.clear();
        offeringFilters.clear();
    };

    const onLogout = () => {
        authService.cancelAllRequests("logged out");
        authService.clearAuthToken();
        timezones.clearTimeZone();

        clearAll();

        router.redirectToInitialPage(history, PUBLIC_PAGES_EXACT, PUBLIC_PAGES_STARTS_WITH, PAGE_LOGIN);
    };

    const bootstrapSuccess = () => {
        if (bootstrap.user.temporary) {
            router.redirectTo(history, PAGE_PASSWORD);

        } else {
            router.redirectToInitialPage(history, PRIVATE_PAGES_EXACT, PRIVATE_PAGES_STARTS_WITH, PAGE_HOME);
        }

        nodes.refresh();
        edges.refresh();

        if (bootstrap.user.roles.admin) {
            users.refresh();

        } else {
            users.empty();
        }

        if (bootstrap.user.roles.admin || bootstrap.user.roles.deacon) {
            offerings.refresh();

        } else {
            offerings.empty();
        }

        if (bootstrap.user.roles.admin || bootstrap.user.roles.secretary) {
            tasks.refresh();

        } else {
            tasks.empty();
        }

        authService.recordLoginIfNecessary(snackbar.setError);
    };

    const bootstrapFailure = (err) => {
        snackbar.setError(err);
        onLogout();
    };

    const bootstrap = useBootstrap(authService, accountService, profileService, notificationService,
        bootstrapSuccess, bootstrapFailure);

    const progress = useProgress(bootstrap,
        [users, nodes, edges, offerings, tasks],
        [],
        0,
        time.MILLISECONDS_IN_SECOND);

    const event = usePushEvent(bootstrap);

    const onLogin = (accounts) => {
        progress.refresh();
        bootstrap.refresh(accounts);
    };

    const onSwitchAccounts = (accounts) => {
        clearAll();
        onLogin(accounts);
        router.redirectTo(history, PAGE_HOME);
    };

    useEffect(() => {
        if (event && event.type) {
            const type = event.type;
            const data = event.payload || {};
            const payload = data.payload || {};
            const actor = data.actor || {};

            console.table(type, data);

            if (bootstrap.user.id !== actor.userId) {
                switch (type) {
                    case "TBD":
                        // todo: do stuff
                    default:
                        break;
                }
            }
        }
    }, [event]);

    useEffect(() => {
        const settings = bootstrap.user ? bootstrap.user.settings || {} : {};
        const theme = settings.theme || themes.LIGHT;
        const branding = brandingUtils.getBranding(theme);

        onThemeChange(theme, branding);
    }, [bootstrap.account, bootstrap.user]);

    useEffect(() => {
        appService.ensureOnLatestVersion(false, null);
        authService.setupAxiosInterceptors(onLogout);
        firebaseUtils.initFirebase(FIREBASE_CONFIG);
        dimensions.onResize();

        if (authService.loggedIn()) {
            onLogin();

        } else {
            onLogout();
        }
    }, []);

    const dateModes = useMemo(() => {
        const ret = {};

        if (bootstrap.account) {
            const settings = objects.defaultIfNullOrUndefined(bootstrap.account.settings.dateModes, {});

            unique.dates.forEach(date => {
                ret[date] = objects.defaultIfNullOrUndefined(settings[date], DATE_YEARLY);
            });
        }

        return ret;
    }, [unique, bootstrap.account]);

    return <div className="app">
        {progress.loading || progress.paused ?
            <Splash
                percent={progress.percent}
            /> : bootstrap.account && bootstrap.user ?
            <>
                {MOCK_PHONE ? <PhoneStatusBar/> : null}
                <div className="content">
                    <Switch>
                        <Route exact path="/">
                            <Redirect to={PAGE_HOME}/>
                        </Route>
                        <Route path={PAGE_NODES}>
                            <NodesPage
                                snackbar={snackbar}
                                dimensions={dimensions}
                                user={bootstrap.user}
                                unique={unique}
                                households={households}
                                nodes={nodes.entities}
                                filters={nodeFilters}
                                onDelete={nodes.onEntityDeleted}
                                onGotoAdd={() => router.redirectTo(history, PAGE_NODE)}
                                onGotoEdit={(nodeId) => router.redirectTo(history, `${PAGE_NODE}/${nodeId}`)}
                            />
                        </Route>
                        <Route exact path={PAGE_NODE}>
                            <AddNodePage
                                snackbar={snackbar}
                                unique={unique}
                                onSave={(node) => {
                                    nodes.onEntitySaved(node);
                                    router.redirectTo(history, PAGE_NODE + "/" + node.id);
                                }}
                            />
                        </Route>
                        <Route
                            path={`${PAGE_NODE}/:id`}
                            render={(props) =>
                                <EditNodePage
                                    {...props}
                                    snackbar={snackbar}
                                    dimensions={dimensions}
                                    account={bootstrap.account}
                                    user={bootstrap.user}
                                    unique={unique}
                                    households={households}
                                    nodes={nodes.entities}
                                    edges={edges.entities}
                                    offerings={offerings.entities}
                                    filters={nodeFilters}
                                    onSaveNode={nodes.onEntitySaved}
                                    onSaveEdge={edges.onEntitySaved}
                                    onSaveOffering={offerings.onEntitySaved}
                                    onDeleteEdge={edges.onEntityDeleted}
                                    onDeleteOffering={offerings.onEntityDeleted}
                                    onGoto={(nodeId) => router.redirectTo(history, PAGE_NODE + "/" + nodeId)}
                                />}
                        />
                        {bootstrap.user.roles.admin || bootstrap.user.roles.deacon ?
                            <Route path={PAGE_OFFERINGS}>
                                <OfferingsPage
                                    snackbar={snackbar}
                                    dimensions={dimensions}
                                    user={bootstrap.user}
                                    unique={unique}
                                    nodes={nodes.entities}
                                    offerings={offerings.entities}
                                    filters={offeringFilters}
                                    onSave={offerings.onEntitySaved}
                                    onDelete={offerings.onEntityDeleted}
                                />
                            </Route> : null}
                        {bootstrap.user.roles.admin || bootstrap.user.roles.secretary ?
                            <Route path={PAGE_CALENDAR}>
                                <CalendarPage
                                    snackbar={snackbar}
                                    dimensions={dimensions}
                                    dateModes={dateModes}
                                    nodes={nodes.entities}
                                    tasks={tasks.entities}
                                    date={date}
                                    setDate={setDate}
                                    onSaveTask={tasks.onEntitySaved}
                                    onDeleteTask={tasks.onEntityDeleted}
                                    onGotoNode={(nodeId) => router.redirectTo(history, `${PAGE_NODE}/${nodeId}`)}
                                />
                            </Route> : null}
                        {bootstrap.user.roles.admin ?
                            <Route path={PAGE_ACCOUNT}>
                                <MyAccountPage
                                    snackbar={snackbar}
                                    account={bootstrap.account}
                                    onSave={bootstrap.syncAccount}
                                    myAccountService={accountService}
                                    settingsForm={AccountSettingsForm}
                                    settingsProps={{
                                        dateModes: dateModes,
                                    }}
                                    settingsLabel="Calendar"
                                    showCode={true}
                                />
                            </Route> : null}
                        {bootstrap.user.roles.admin ?
                            <Route path={PAGE_USERS}>
                                <UsersPage
                                    snackbar={snackbar}
                                    dimensions={dimensions}
                                    user={bootstrap.user}
                                    users={users.entities}
                                    filters={userFilters}
                                    onDelete={users.onEntityDeleted}
                                    onGotoAdd={() => router.redirectTo(history, PAGE_USER)}
                                    onGotoProfile={() => router.redirectTo(history, PAGE_SETTINGS)}
                                    onGotoUser={(userId) => router.redirectTo(history, `${PAGE_USER}/${userId}`)}
                                    userService={userService}
                                    userCard={UserCard}
                                    userFilters={UserFilters}
                                />
                            </Route> : null}
                        {bootstrap.user.roles.admin ?
                            <Route exact path={PAGE_USER}>
                                <AddUserPage
                                    snackbar={snackbar}
                                    onSave={(user) => {
                                        users.onEntitySaved(user);
                                        router.redirectTo(history, PAGE_USER + "/" + user.id);
                                    }}
                                />
                            </Route> : null}
                        {bootstrap.user.roles.admin ?
                            <Route
                                path={`${PAGE_USER}/:id`}
                                render={(props) =>
                                    <EditUserPage
                                        {...props}
                                        snackbar={snackbar}
                                        user={bootstrap.user}
                                        users={users.entities}
                                        filters={userFilters}
                                        onSave={users.onEntitySaved}
                                        onPrev={(userId) => router.redirectTo(history, PAGE_USER + "/" + userId)}
                                        onNext={(userId) => router.redirectTo(history, PAGE_USER + "/" + userId)}
                                    />}
                            /> : null}
                        <Route path={PAGE_SETTINGS}>
                            <ProfilePage
                                snackbar={snackbar}
                                user={bootstrap.user}
                                onSaveProfile={bootstrap.syncProfile}
                                profileService={profileService}
                                settingsForm={UserSettingsForm}
                                showImage={true}
                                showBirthday={true}
                                allowApp={true}
                                allowEmail={true}
                                onDeleteProfile={onLogout}
                            />
                        </Route>
                        <Route path={PAGE_PASSWORD}>
                            <ChangePasswordPage
                                snackbar={snackbar}
                                user={bootstrap.user}
                                authService={authService}
                                onSaveProfile={bootstrap.syncProfile}
                                onToHome={() => router.redirectTo(history, PAGE_HOME)}
                            />
                        </Route>
                        <Route path={PAGE_ABOUT}>
                            <AboutPage
                                appName={APP_NAME}
                                appDescription={APP_TAG_LINE}
                                appService={appService}
                                account={bootstrap.account}
                                user={bootstrap.user}
                                src={icon}
                            />
                        </Route>
                        <Route path={PAGE_JOIN_ACCOUNT}>
                            <JoinAccountPage
                                snackbar={snackbar}
                                accounts={bootstrap.accounts}
                                onSwitchAccounts={(accounts) => onSwitchAccounts(accounts)}
                                authService={authService}
                                accountService={accountService}
                            />
                        </Route>
                        <Route path={PAGE_LOGIN}>
                            <Redirect to={PAGE_HOME}/>
                        </Route>
                        <Route>
                            <NotFoundPage/>
                        </Route>
                    </Switch>
                </div>
                <Footer
                    snackbar={snackbar}
                    dimensions={dimensions}
                    accounts={bootstrap.accounts}
                    account={bootstrap.account}
                    user={bootstrap.user}
                    onLogout={onLogout}
                    onSwitchAccounts={() => onSwitchAccounts(bootstrap.accounts)}
                />
            </> :
            <>
                <PublicHeader
                    dimensions={dimensions}
                    appName={APP_NAME}
                    src={icon}
                />
                <div className="content">
                    <Switch>
                        <Route path={PAGE_LOGIN}>
                            <LoginPage
                                snackbar={snackbar}
                                authService={authService}
                                onForgotPassword={() => router.redirectTo(history, PAGE_RECOVERY)}
                                onNewUser={() => router.redirectTo(history, PAGE_NEW_USER)}
                                onLogin={onLogin}
                            />
                        </Route>
                        <Route path={PAGE_RECOVERY}>
                            <ForgotPasswordPage
                                snackbar={snackbar}
                                authService={authService}
                                onBackToLogin={() => router.redirectTo(history, PAGE_LOGIN)}
                                onLogin={onLogin}
                                appName={APP_NAME}
                            />
                        </Route>
                        <Route path={`${PATH_DEEP_LINK}${PAGE_WELCOME}`}>
                            <WelcomePage
                                snackbar={snackbar}
                                authService={authService}
                                onToLogin={() => router.redirectTo(history, PAGE_LOGIN)}
                                onLogin={onLogin}
                            />
                        </Route>
                        <Route path={PAGE_NEW_USER}>
                            <NewUserPage
                                snackbar={snackbar}
                                authService={authService}
                                accountService={accountService}
                                onBackToLogin={() => router.redirectTo(history, PAGE_LOGIN)}
                                onLogin={onLogin}
                                showCode={true}
                            />
                        </Route>
                    </Switch>
                </div>
            </>}
        </div>
}

export default withRouter(App);
